import debugFn from 'debug';
import Hls from 'hls.js';
import Player from 'video.js/dist/types/player';
import { livePreset, vodPreset } from './presets';
import { HlsJsPreset } from './types';

const debug = debugFn('setupHlsJs');

/**
 * Mapping of preset names to preset configurations
 */
const presets: Record<string, HlsJsPreset> = {
  live: livePreset,
  vod: vodPreset,
};

/**
 * Sets up HLS.js for video playback with optimized configuration based on preset.
 * Configures buffer management, latency optimization, and memory limits.
 *
 * @param player - The Video.js player instance
 * @param src - The HLS source URL
 * @param preset - The preset name to use ('live' or 'vod'). Defaults to 'vod'
 */
export function setupHlsJs(player: Player, src: string, preset: string = 'vod'): void {
  debug('hls.js is supported, setting up hls.js');

  const selectedPreset = presets[preset] || vodPreset;

  if (!presets[preset]) {
    console.warn(`Unknown preset "${preset}", using "vod" preset`);
  }

  const hls = new Hls({
    // debug: true,
    ...selectedPreset.config,
  });

  debug('using preset %s', preset);

  const videoEl = player.tech(false).el() as HTMLMediaElement;

  // Wire it up
  hls.attachMedia(videoEl);
  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    hls.loadSource(src);
  });

  // Run preset-specific setup function if provided
  if (selectedPreset.setup) {
    selectedPreset.setup(hls, player);
  }

  // Expose for debugging (levels, stats, etc.)
  // @ts-expect-error hls property exists but not in the videojs ts types
  player.hls = hls;

  // Keep memory clean
  player.one('dispose', () => hls.destroy());
}
